import { Wrench, CheckCircle, Clock, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const MobileMechanicPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Mobile Mechanic Hackney | On-Site Car Repairs East London | Mobile Car Service"
        description="Mobile mechanic service in Hackney & East London. On-site diagnostics, repairs, servicing at your home or work. Fast response, quality work. Call 07492 639223 for mobile mechanic."
        keywords="mobile mechanic hackney, mobile car repairs, on-site car service, mobile diagnostics, mobile mechanic east london, home car repairs, mobile vehicle servicing"
      />
      <Header />

      <section className="bg-gradient-to-br from-blue-900 via-indigo-800 to-violet-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🔧 We Come To You
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Mobile Mechanic Service in Hackney & East London
            </h1>
            <p className="text-xl text-indigo-100 mb-8">
              Professional mobile mechanic service at your home, work, or roadside. On-site diagnostics, repairs, and servicing across Hackney and East London. No need to visit a garage - we bring the workshop to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-indigo-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Wrench className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-900 text-lg px-8 py-6">
                  Book Mobile Service
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-indigo-600">Same Day</p>
              <p className="text-sm text-slate-600">Service Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-indigo-600">Home/Work</p>
              <p className="text-sm text-slate-600">We Come To You</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-indigo-600">Qualified</p>
              <p className="text-sm text-slate-600">Experienced Mechanics</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-indigo-600">Guaranteed</p>
              <p className="text-sm text-slate-600">Work Warranty</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Mobile Mechanic Services
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Our qualified mobile mechanics bring professional car repair and servicing to your doorstep across Hackney and East London. Whether you're at home, work, or broken down roadside, we come equipped with tools and expertise to fix your vehicle on-site. Save time and hassle - no need to visit a garage or arrange transport.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Mobile Services We Provide
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "On-site vehicle diagnostics",
                "Engine diagnostics & fault finding",
                "Oil & filter changes",
                "Brake inspections & pad replacement",
                "Battery testing & replacement",
                "Alternator & starter motor repairs",
                "Clutch adjustments",
                "Tyre replacement & puncture repairs",
                "Pre-MOT checks",
                "General servicing & maintenance",
                "Fluid top-ups & checks",
                "Light bulb replacements"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Wrench className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Benefits of Mobile Mechanic Service
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-indigo-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Save Time & Hassle</h4>
                      <p className="text-slate-600">
                        No need to take time off work, arrange lifts, or sit in waiting rooms. We work around your schedule at a location convenient for you - home, work, or roadside.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Wrench className="w-8 h-8 text-indigo-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Professional Equipment</h4>
                      <p className="text-slate-600">
                        Our mobile mechanics arrive fully equipped with professional diagnostic tools, quality parts, and everything needed to complete repairs on-site to the highest standard.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-indigo-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Competitive Pricing</h4>
                      <p className="text-slate-600">
                        Without garage overheads, we offer competitive rates for mobile servicing. Transparent pricing with no hidden fees. Get a quote before we start work.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Areas Covered by Mobile Mechanics
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Hackney E5, E8, E9",
                "Shoreditch E1, E2",
                "Stratford E15, E20",
                "Bethnal Green E2",
                "Islington N1",
                "Tower Hamlets E1-E14",
                "Bow & Mile End E3",
                "Dalston E8",
                "All East London",
                "Stoke Newington N16",
                "Clapton E5",
                "15-mile radius"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-indigo-900 to-violet-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need a Mobile Mechanic?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Professional mobile mechanic service across Hackney & East London. We come to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-indigo-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Wrench className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-900 text-lg px-8 py-6">
                Book Service
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
;
